import { useTrackers } from '@ps/web-shared-analytics';
import type { RunnerId } from '@ps/web-sports-api-types';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Selection, SelectionWithTracking } from '@swf/core/models/sports/types/betSlip';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

export type BetSlipItem = Selection;

export const MAX_BET_SLIP_SIZE = 25;

const buildBetSlipItemId = ({ marketId, selectionId }: RunnerId) => `${marketId}_${selectionId}`;

export default () => {
  const dispatch = useDispatch<Dispatch>();
  const isBetSlipLocked = useSelector(select.betSlip.isLocked);
  const betSlip = useSelector(select.betSlip.selections);
  const { addedToBetslip, betslipCleared, selectionRemoved } = useTrackers();

  const addSelectionToBetSlip = useCallback(
    (item: SelectionWithTracking) => {
      if (isBetSlipLocked || betSlip.length >= MAX_BET_SLIP_SIZE) {
        return;
      }

      const { selectionId, marketId, eventId, legType } = item;
      addedToBetslip(item);
      dispatch.betSlip.selectionAdded({
        selectionId,
        marketId,
        eventId,
        legType
      });
    },
    [dispatch, betSlip, isBetSlipLocked, addedToBetslip]
  );

  const removeSelectionFromBetSlip = useCallback(
    (runnerId: RunnerId) => {
      if (isBetSlipLocked) {
        return;
      }
      dispatch.betSlip.removeSelectionWithTracking({
        runnerId,
        selectionRemovedTrackingMethod: selectionRemoved
      });
    },
    [isBetSlipLocked, dispatch.betSlip, selectionRemoved]
  );

  const isSelectionInTheBetSlip = useCallback(
    (runnerId: RunnerId): boolean =>
      !!betSlip.find((item) => buildBetSlipItemId(item) === buildBetSlipItemId(runnerId)),
    [betSlip]
  );

  const isEverySelectionInTheBetSlip = useCallback(
    (runnerIds: RunnerId[]) => runnerIds.every(isSelectionInTheBetSlip),
    [isSelectionInTheBetSlip]
  );

  const clearBetSlip = useCallback(() => {
    dispatch.betSlip.clearBetslipWithTracking(betslipCleared);
  }, [dispatch.betSlip, betslipCleared]);

  const addSelectionsToBetSlip = useCallback(
    (items: SelectionWithTracking[]) => {
      const nonBetSlipItems = items.filter(
        ({ marketId, selectionId }) => !isSelectionInTheBetSlip({ marketId, selectionId })
      );

      if (
        isBetSlipLocked ||
        betSlip.length >= MAX_BET_SLIP_SIZE ||
        betSlip.length + nonBetSlipItems.length > MAX_BET_SLIP_SIZE
      ) {
        return;
      }

      nonBetSlipItems.forEach((item) => {
        addedToBetslip(item);
      });

      const selections = nonBetSlipItems.map(({ selectionId, marketId, legType, eventId }) => ({
        selectionId,
        marketId,
        legType,
        eventId
      }));

      dispatch.betSlip.selectionsAdded(selections);
    },
    [dispatch, betSlip, isBetSlipLocked, addedToBetslip, isSelectionInTheBetSlip]
  );

  const removeSelectionsFromBetSlip = useCallback(
    (runnerIds: RunnerId[]) => {
      if (isBetSlipLocked) {
        return;
      }

      dispatch.betSlip.removeSelectionsWithTracking({
        runnerIds,
        selectionRemovedTrackingMethod: selectionRemoved
      });
    },
    [isBetSlipLocked, dispatch.betSlip, selectionRemoved]
  );

  return {
    addSelectionsToBetSlip,
    addSelectionToBetSlip,
    isEverySelectionInTheBetSlip,
    betSlip,
    clearBetSlip,
    isSelectionInTheBetSlip,
    removeSelectionFromBetSlip,
    removeSelectionsFromBetSlip
  };
};
